import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  const values = [
    {
      icon: 'ri-heart-line',
      title: 'Compassion',
      description: 'We approach every individual with empathy, understanding, and unconditional support throughout their recovery journey.',
      gradient: 'from-rose-500 to-pink-500',
    },
    {
      icon: 'ri-moon-line',
      title: 'Faith-Centered',
      description: 'Islamic principles and spiritual guidance form the foundation of our approach to healing and recovery.',
      gradient: 'from-teal-500 to-emerald-500',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Confidentiality',
      description: 'Complete privacy and discretion are guaranteed. Your trust and safety are our highest priorities.',
      gradient: 'from-blue-500 to-indigo-500',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative h-[60vh] flex items-center justify-center bg-gradient-to-br from-purple-900 via-teal-800 to-teal-600">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <nav className="text-sm text-teal-100 mb-6">
            <span>Home</span>
            <i className="ri-arrow-right-s-line mx-2"></i>
            <span>About</span>
          </nav>
          <h1 className="text-6xl md:text-7xl font-serif font-bold mb-6">
            Our Story & Mission
          </h1>
          <div className="text-4xl font-serif opacity-30 mb-8">شفاء</div>
          <div className="grid md:grid-cols-2 gap-8 text-lg text-teal-50">
            <p>
              Founded on the principles of compassion and Islamic guidance, we provide a safe haven for Muslims seeking freedom from addiction.
            </p>
            <p>
              Our mission is to heal both the mind and soul, helping individuals reconnect with their faith while overcoming life's greatest challenges.
            </p>
          </div>
        </div>
      </section>

      {/* Question Hook & Assessment CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Question Side */}
            <div className="text-center md:text-left">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6 leading-tight">
                Do you want to know where I would start if I wanted to reduce your addictive patterns as fast as possible?
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-emerald-500 mx-auto md:mx-0"></div>
            </div>

            {/* Assessment CTA Side */}
            <div className="bg-gradient-to-br from-teal-600 to-emerald-600 rounded-3xl p-10 text-white shadow-2xl">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mb-6">
                <i className="ri-calendar-check-line text-4xl text-white"></i>
              </div>
              <h3 className="text-3xl font-bold mb-4">
                Book Your Free Addiction Assessment
              </h3>
              <p className="text-lg text-teal-50 mb-6 leading-relaxed">
                Get a 7 day action plan to not relapse and find out why you're still relapsing in a confidential, judgment-free call.
              </p>
              <a
                href="https://calendly.com/coachabdurrahman1/why-your-still-relapsing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full px-8 py-4 bg-white text-teal-700 text-lg font-bold rounded-full hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Book Your Assessment
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* You're in the Right Place Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-8 leading-tight">
              You're in the Right Place Because…
            </h2>
            <div className="space-y-6 text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              <p>
                You're not lacking willpower or sincerity.<br />
                You're stuck in a cycle that guilt and generic advice haven't been able to fix.
              </p>
              <p className="text-2xl font-semibold text-gray-900 mt-8">
                If you're tired of repeating the same pattern — intention, relapse, shame, repeat — you're in the right place.
              </p>
            </div>
          </div>

          {/* I Understand Because */}
          <div className="mb-16">
            <h3 className="text-4xl font-serif font-bold text-gray-900 mb-6 text-center">
              I Understand Because…
            </h3>
            <div className="bg-white rounded-3xl p-10 shadow-lg">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                For the past 6 years, I've worked one-on-one with Muslim men struggling with addiction — privately and anonymously.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                As a former teacher with 2,000+ hours of mentoring, I understand how capable, practicing men stay trapped when they don't have a clear, practical process for change.
              </p>
            </div>
          </div>

          {/* I Know Change Can Happen */}
          <div>
            <h3 className="text-4xl font-serif font-bold text-gray-900 mb-6 text-center">
              I Know Change Can Happen Because…
            </h3>
            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-10 border-2 border-teal-200">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                When men stop relying on willpower and start working with structure — habits, emotional skills, environment, and mindset — progress becomes steady and sustainable.
              </p>
              <div className="text-center mt-8">
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  This isn't about perfection.
                </p>
                <p className="text-2xl font-bold text-teal-700">
                  It's about consistency and integrity.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Organizations Banner */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Organisations & Clients I've Worked With
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Alongside private coaching, I've supported individuals connected to educational institutions, community organisations, and professional environments where discretion, trust, and results matter.
            </p>
            <p className="text-base text-gray-600 mt-4 max-w-3xl mx-auto">
              My work is built on long-term change, not quick fixes — and always delivered with confidentiality.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-8 shadow-lg flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/8a86924c37578583a2eb4b2d060c5a4d/711387527e2dc3ab47ba6a5cdbb39387.png"
                alt="Organizations and Clients"
                className="w-full h-auto max-w-md"
              />
            </div>
            
            <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-8 shadow-lg flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/8a86924c37578583a2eb4b2d060c5a4d/cec739a3f9a357b268eb3c9a730a2fd4.webp"
                alt="MyTazkiyah Purification"
                className="w-full h-auto max-w-md"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Founder Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-3xl opacity-20"></div>
                <img
                  src="https://static.readdy.ai/image/8a86924c37578583a2eb4b2d060c5a4d/b849fd7eb7ea2cb067ce318d0a25c324.png"
                  alt="Coach Abdurrahman"
                  className="relative rounded-3xl shadow-2xl w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <div className="inline-block px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-medium mb-6">
                The Journey
              </div>
              <h2 className="text-5xl font-serif font-bold text-gray-900 mb-8 leading-tight">
                A Mission Born from Understanding
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p className="first-letter:text-6xl first-letter:font-serif first-letter:font-bold first-letter:text-teal-600 first-letter:float-left first-letter:mr-3 first-letter:leading-none">
                  My coaching experience comes from completing 12-step programmes, CBT, NLP training, a general coaching qualification, and two other addiction programmes, as well as receiving personal mentoring in the field of addiction from the likes of Wael Ibrahim.
                </p>
                <p>
                  This comprehensive foundation allows me to integrate evidence-based methods with Islamic principles, creating a holistic approach that addresses both the psychological and spiritual dimensions of recovery.
                </p>
                <div className="bg-teal-50 border-l-4 border-teal-600 p-6 my-8 rounded-r-lg">
                  <p className="italic text-xl text-gray-800 mb-6">
                    "True healing addresses not just the symptoms, but the soul. When we reconnect with Allah and understand our purpose, recovery becomes not just possible, but transformative."
                  </p>
                  <div className="pt-6 border-t border-teal-200">
                    <p className="text-lg text-gray-700 mb-2 font-arabic">
                      إِنَّ ٱللَّهَ لَا يُغَيِّرُ مَا بِقَوْمٍ حَتَّىٰ يُغَيِّرُوا۟ مَا بِأَنفُسِهِمْ
                    </p>
                    <p className="text-base text-gray-600 italic">
                      "Indeed, Allah will not change the condition of a people until they change what is in themselves."
                    </p>
                    <p className="text-sm text-teal-700 font-medium mt-2">
                      — Surah Ar-Ra'd (13:11)
                    </p>
                  </div>
                </div>
                <p>
                  Today, our program has helped hundreds of individuals and families find their way back to health, happiness, and spiritual fulfillment. Every success story reinforces our belief that with the right support, anyone can overcome addiction.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-teal-600 to-emerald-600 rounded-3xl p-12 md:p-16 text-white text-center shadow-2xl">
            <div className="text-5xl font-serif mb-6 opacity-50">بسم الله</div>
            <h2 className="text-4xl md:text-5xl font-serif font-bold mb-8">
              Our Mission
            </h2>
            <p className="text-xl md:text-2xl leading-relaxed mb-8 text-teal-50">
              To provide compassionate, faith-centered support that empowers Muslims to overcome addiction, rebuild their lives, and strengthen their connection with Allah. We believe that recovery is not just about abstinence, but about rediscovering purpose, peace, and spiritual fulfillment.
            </p>
            <div className="grid md:grid-cols-2 gap-8 mt-12">
              <div>
                <div className="text-5xl font-bold mb-2">500+</div>
                <div className="text-teal-100">Lives Transformed</div>
              </div>
              <div>
                <div className="text-5xl font-bold mb-2">6+</div>
                <div className="text-teal-100">Years of Service</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-slate-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6 leading-tight">
            Ready to Start Your Recovery Journey?
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Book your free addiction assessment and get a 7 day action plan to not relapse — discover why you're still relapsing and get a clear path forward.
          </p>
          <a
            href="https://calendly.com/coachabdurrahman1/why-your-still-relapsing"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white text-lg font-bold rounded-full hover:from-teal-700 hover:to-emerald-700 transition-all shadow-xl hover:shadow-2xl cursor-pointer whitespace-nowrap"
          >
            Book Your Free Assessment
            <i className="ri-arrow-right-line ml-2"></i>
          </a>
        </div>
      </section>

      {/* Credentials Banner */}
      <section className="py-16 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <i className="ri-award-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Coaching Qualification</h3>
              <p className="text-sm text-gray-600">Professional certified coach</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                <i className="ri-brain-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">NLP</h3>
              <p className="text-sm text-gray-600">Neuro-Linguistic Programming</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                <i className="ri-mental-health-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">CBT</h3>
              <p className="text-sm text-gray-600">Cognitive Behavioral Therapy</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4">
                <i className="ri-footprint-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">12 Step Methodology</h3>
              <p className="text-sm text-gray-600">Experienced practitioner</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
